import React, { useState, useEffect } from "react";
import DoctorChip from "./DoctorChip";
import * as ApiService from "../services/ApiService";
import { BsX, BsTrash } from "react-icons/bs";
import moment from "moment";
import "./ProposedScheduleModal.css";
import { useAuth0 } from "@auth0/auth0-react";

const ProposedScheduleModal = ({
  isOpen,
  onClose,
  proposedSchedule,
  startDate,
  endDate,
  onFinalize,
  holidays = [],
  isGenerating = false,

  isPrepopulating = false,
  resetOnClose = false,
}) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [eligibleDoctors, setEligibleDoctors] = useState([]);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);
  const [ruleViolations, setRuleViolations] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [lastReplaced, setLastReplaced] = useState(null);
  const [preSchedule, setPreSchedule] = useState({});
  const { getAccessTokenSilently } = useAuth0();

  // Convert proposedSchedule object to array for roster view
  const scheduleArray = [];
  const allCallLists = new Set();

  // If we're prepopulating, use preSchedule or initialize empty
  // Otherwise use the provided proposedSchedule
  const effectiveSchedule = isPrepopulating ? preSchedule : proposedSchedule;

  // Only process the schedule if it exists
  if (effectiveSchedule) {
    Object.entries(effectiveSchedule).forEach(([date, assignments]) => {
      assignments.forEach((assignment) => {
        allCallLists.add(assignment.callList.name);
      });
      scheduleArray.push({
        date,
        assignments,
      });
    });

    // Sort the array by date to ensure chronological order
    scheduleArray.sort((a, b) => moment(a.date).diff(moment(b.date)));
  }

  // Function to fetch active call lists
  const fetchActiveCallLists = async () => {
    try {
      const data = await ApiService.fetchCallLists(getAccessTokenSilently);
      return data.filter((callList) => callList.active);
    } catch (error) {
      console.error("Error fetching active call lists:", error);
      return [];
    }
  };

  // Initialize pre-population grid if in pre-population mode
  useEffect(() => {
    if (isOpen && isPrepopulating && Object.keys(preSchedule).length === 0) {
      const initializePreSchedule = async () => {
        try {
          // Get all dates in the range
          const startMoment = moment(startDate);
          const endMoment = moment(endDate);
          const dateArray = [];

          let currentDate = startMoment.clone();
          while (currentDate.isSameOrBefore(endMoment)) {
            dateArray.push(currentDate.format("YYYY-MM-DD"));
            currentDate.add(1, "days");
          }

          // Get active call lists
          const activeCallLists = await fetchActiveCallLists();

          // Create empty schedule grid
          const emptySchedule = {};

          dateArray.forEach((date) => {
            emptySchedule[date] = activeCallLists.map((callList) => ({
              callList: {
                callListId: callList.callListTypeId,
                name: callList.name,
              },
              doctor: null,
            }));
          });

          setPreSchedule(emptySchedule);
        } catch (error) {
          console.error("Error initializing pre-population schedule:", error);
        }
      };

      initializePreSchedule();
    }
  }, [isOpen, isPrepopulating, startDate, endDate, preSchedule]);

  useEffect(() => {
    // Reset state when modal opens
    setSelectedCell(null);
    setSelectedReplacement(null);
    setEligibleDoctors([]);
    setRuleViolations([]);
    setShowConfirmation(false);

    // Add initial validation when modal opens with a schedule (not for pre-population)
    if (isOpen && proposedSchedule && !isPrepopulating) {
      const validateInitialSchedule = async () => {
        try {
          // Get the first assignment to use as a reference point for validation
          const firstDate = Object.keys(proposedSchedule)[0];
          const firstAssignment = proposedSchedule[firstDate][0];

          const { violations } = await ApiService.validateAssignment(
            getAccessTokenSilently,
            {
              date: firstDate,
              staffId: firstAssignment.doctor.staffId,
              callListId: firstAssignment.callList.callListId,
              proposedSchedule: proposedSchedule,
            }
          );
          setRuleViolations(violations);
        } catch (error) {
          console.error("Error validating initial schedule:", error);
        }
      };

      validateInitialSchedule();
    }
  }, [isOpen, proposedSchedule, isPrepopulating]);

  useEffect(() => {
    // Clear the last replaced doctor after animation
    if (lastReplaced) {
      const timer = setTimeout(() => {
        setLastReplaced(null);
      }, 600); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [lastReplaced]);

  // Handle click on empty cell in pre-population mode
  const handleEmptyCellClick = async (date, callList) => {
    try {
      setSelectedCell({
        date,
        callList,
        currentDoctor: null,
      });
      setIsLoadingDoctors(true);
      // const doctors = await ApiService.getEligibleDoctors(callList.callListId);
      const doctors = await ApiService.getActiveDoctors(getAccessTokenSilently);
      setEligibleDoctors(doctors);
    } catch (error) {
      console.error("Error fetching eligible doctors:", error);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Modified doctor click handler to work in both modes
  const handleDoctorClick = async (date, callList, currentDoctor) => {
    try {
      setSelectedCell({
        date,
        callList,
        currentDoctor: currentDoctor
          ? {
              staffId: currentDoctor.staffId,
              abbreviation: currentDoctor.abbreviation,
            }
          : null,
      });
      setIsLoadingDoctors(true);
      // const doctors = await ApiService.getEligibleDoctors(callList.callListId);
      const doctors = await ApiService.getActiveDoctors(getAccessTokenSilently);
      setEligibleDoctors(doctors);
    } catch (error) {
      console.error("Error fetching eligible doctors:", error);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Handle removing a pre-populated assignment
  const handleRemoveAssignment = (date, callListId) => {
    const updatedSchedule = { ...preSchedule };
    const daySchedule = updatedSchedule[date];
    const assignmentIndex = daySchedule.findIndex(
      (a) => a.callList.callListId === callListId
    );

    if (assignmentIndex !== -1) {
      daySchedule[assignmentIndex] = {
        ...daySchedule[assignmentIndex],
        doctor: null,
      };

      setPreSchedule(updatedSchedule);

      // If rule violations exist, revalidate
      if (ruleViolations.length > 0) {
        validateSchedule(updatedSchedule);
      }
    }
  };

  // Validate the schedule for rule violations
  const validateSchedule = async (scheduleToValidate) => {
    try {
      // Find a non-null doctor assignment to use as reference
      let referenceDate, referenceAssignment;

      for (const [date, assignments] of Object.entries(scheduleToValidate)) {
        const docAssignment = assignments.find((a) => a.doctor !== null);
        if (docAssignment) {
          referenceDate = date;
          referenceAssignment = docAssignment;
          break;
        }
      }

      // If no assignments, clear violations
      if (!referenceAssignment) {
        setRuleViolations([]);
        return;
      }

      const { violations } = await ApiService.validateAssignment(
        getAccessTokenSilently,
        {
          date: referenceDate,
          staffId: referenceAssignment.doctor.staffId,
          callListId: referenceAssignment.callList.callListId,
          proposedSchedule: scheduleToValidate,
        }
      );

      setRuleViolations(violations);
    } catch (error) {
      console.error("Error validating schedule:", error);
    }
  };

  // Modified doctor select handler to work with pre-population
  const handleDoctorSelect = async (newDoctor) => {
    if ((!proposedSchedule && !isPrepopulating) || !selectedCell) return;

    setSelectedReplacement(newDoctor);

    // Update the schedule based on mode
    const updatedSchedule = isPrepopulating
      ? { ...preSchedule }
      : { ...proposedSchedule };

    const daySchedule = updatedSchedule[selectedCell.date];
    const assignmentIndex = daySchedule.findIndex(
      (a) => a.callList.callListId === selectedCell.callList.callListId
    );

    if (assignmentIndex !== -1) {
      daySchedule[assignmentIndex] = {
        ...daySchedule[assignmentIndex],
        doctor: {
          staffId: newDoctor.staffId,
          abbreviation: newDoctor.abbreviation,
        },
      };

      // Set the last replaced doctor info
      setLastReplaced({
        date: selectedCell.date,
        callListId: selectedCell.callList.callListId,
        doctorId: newDoctor.staffId,
      });

      // Update the appropriate state
      if (isPrepopulating) {
        setPreSchedule(updatedSchedule);
      }
    }

    // Validate the new assignment
    try {
      const { violations } = await ApiService.validateAssignment(
        getAccessTokenSilently,
        {
          date: selectedCell.date,
          staffId: newDoctor.staffId,
          callListId: selectedCell.callList.callListId,
          proposedSchedule: updatedSchedule,
        }
      );
      setRuleViolations(violations);
    } catch (error) {
      console.error("Error validating assignment:", error);
    }

    // Clear selections after replacement
    setSelectedCell(null);
    setSelectedReplacement(null);
  };

  // Handle continuing to generation with pre-populated schedule
  const handleContinueToGeneration = () => {
    // Filter out empty assignments
    const filteredSchedule = {};

    Object.entries(preSchedule).forEach(([date, assignments]) => {
      const nonEmptyAssignments = assignments.filter((a) => a.doctor !== null);

      if (nonEmptyAssignments.length > 0) {
        filteredSchedule[date] = nonEmptyAssignments;
      }
    });

    // Call onFinalize with the pre-populated schedule
    onFinalize(filteredSchedule);
  };

  // Original handleFinalize for non-prepopulation mode
  const handleFinalize = async () => {
    if (!proposedSchedule && !isPrepopulating) return;

    if (isPrepopulating) {
      handleContinueToGeneration();
      return;
    }

    if (ruleViolations.length > 0) {
      setShowConfirmation(true);
    } else {
      try {
        await onFinalize(proposedSchedule);
        onClose();
      } catch (error) {
        console.error("Error finalizing schedule:", error);
        alert("Failed to finalize schedule. Please try again.");
      }
    }
  };

  // Rest of the existing code remains the same
  const handleConfirmFinalize = async () => {
    try {
      await onFinalize(proposedSchedule);
      setShowConfirmation(false);
      onClose();
    } catch (error) {
      console.error("Error finalizing schedule:", error);
      alert("Failed to finalize schedule. Please try again.");
    }
  };

  const formatViolationMessage = (violation) => {
    const { rule_key, data } = violation;
    const { doctorAbbrev, dates } = data;

    const boldDoctor = `**${doctorAbbrev}**`;
    const boldDates = dates.map((date) => `**${date}**`);

    switch (rule_key) {
      case "no_calls_on_leave":
        return `${boldDoctor} is on leave on ${boldDates[0]}`;

      case "seniors_weekdays_only":
        const dateObj = new Date(dates[0]);

        // Get day type from the violation data
        const dayType = violation.data.dayType;

        if (dayType === "publicHoliday") {
          return `${boldDoctor} is a <span class="senior">SENIOR</span> doctor and cannot be assigned on a Public Holiday ${boldDates[0]}`;
        } else if (dayType) {
          // Capitalize first letter of day name
          const capitalizedDay =
            dayType.charAt(0).toUpperCase() + dayType.slice(1);
          return `${boldDoctor} is a <span class="senior">SENIOR</span> doctor and cannot be assigned on a ${capitalizedDay} ${boldDates[0]}`;
        } else {
          // Fallback to client-side day detection if dayType not provided
          const dayOfWeek = dateObj.toLocaleDateString("en-US", {
            weekday: "long",
          });
          const isHoliday = holidays.some(
            (h) => moment(h).format("YYYY-MM-DD") === dates[0]
          );

          if (isHoliday) {
            return `${boldDoctor} is a <span class="senior">SENIOR</span> doctor and cannot be assigned on a Public Holiday ${boldDates[0]}`;
          } else {
            return `${boldDoctor} is a <span class="senior">SENIOR</span> doctor and cannot be assigned on a ${dayOfWeek} ${boldDates[0]}`;
          }
        }

      case "no_multiple_calls_a_day":
        return `${boldDoctor} is assigned to multiple call lists on ${boldDates[0]}`;

      case "no_back_to_back_calls":
        return `${boldDoctor} has calls too close together on ${boldDates[0]} and ${boldDates[1]}`;

      case "one_call_per_holiday_block":
        return `${boldDoctor} has multiple assignments in a holiday block: ${
          boldDates.length === 2
            ? `${boldDates[0]} and ${boldDates[1]}`
            : `${boldDates.slice(0, -1).join(", ")}, and ${
                boldDates[boldDates.length - 1]
              }`
        }`;

      default:
        return "Unknown rule violation";
    }
  };

  const renderViolationMessage = (message) => {
    // First convert the HTML string to JSX elements
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = message;
    const htmlContent = tempDiv.childNodes;

    // Process each node and handle both HTML elements and text with ** markers
    return Array.from(htmlContent).map((node, index) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        // Handle HTML elements (like our span)
        return React.createElement(
          node.tagName.toLowerCase(),
          { key: index, className: node.className },
          node.textContent
        );
      } else {
        // Handle text nodes with ** markers
        const parts = node.textContent.split(/(\*\*[^*]+\*\*)/);
        return parts.map((part, partIndex) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <span
                key={`${index}-${partIndex}`}
                style={{ fontWeight: "bold" }}
              >
                {part.slice(2, -2)}
              </span>
            );
          }
          return <span key={`${index}-${partIndex}`}>{part}</span>;
        });
      }
    });
  };

  const isViolatingDoctor = (date, doctorId) => {
    if (!ruleViolations) return false;
    return ruleViolations.some((violation) => {
      const { data } = violation;
      // Check if the doctor matches and either:
      // 1. The current date is in the violation dates, or
      // 2. For back-to-back violations, check if the current date is either of the consecutive dates
      return (
        data.doctorId === doctorId &&
        (data.dates.includes(date) ||
          (violation.rule_key === "no_back_to_back_calls" &&
            data.dates.some((d) => moment(d).diff(moment(date), "days") === 0)))
      );
    });
  };

  const getDoctorChipClassName = (date, assignment) => {
    const isSelected =
      selectedCell?.date === date &&
      selectedCell?.callList.callListId === assignment.callList.callListId;
    const isViolating = isViolatingDoctor(date, assignment.doctor.staffId);
    const isJustReplaced =
      lastReplaced &&
      lastReplaced.date === date &&
      lastReplaced.callListId === assignment.callList.callListId &&
      lastReplaced.doctorId === assignment.doctor.staffId;

    return `${isSelected ? "selected" : ""} ${isViolating ? "violation" : ""} ${
      isJustReplaced ? "just-replaced" : ""
    }`.trim();
  };

  // Add a custom close handler to reset state if needed
  const handleClose = () => {
    // Call the original onClose function
    onClose();

    // Reset preSchedule state if resetOnClose is true
    if (resetOnClose) {
      setPreSchedule({});
    }
  };

  if (!isOpen) return null;

  // Show loading state when generating
  if (isGenerating) {
    return (
      <div className="proposed-schedule-modal-overlay">
        <div className="proposed-schedule-modal-content generating">
          <button
            className="proposed-schedule-modal-close"
            onClick={handleClose}
          >
            <BsX />
          </button>
          <div className="generation-optimizer">
            <div className="optimizer-grid">
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
              <div className="optimizer-square"></div>
            </div>
            <div className="optimizer-text">Optimizing your schedule...</div>
          </div>
        </div>
      </div>
    );
  }

  // Return early if no schedule available, except in pre-population mode
  if (!effectiveSchedule && !isPrepopulating) return null;

  const callListNames = Array.from(allCallLists).sort();

  return (
    <div className="proposed-schedule-modal-overlay">
      <div className="proposed-schedule-modal-content">
        <div className="proposed-schedule-modal-header">
          <h2 className="proposed-schedule-modal-title">
            {isPrepopulating ? "Pre-populate Schedule" : "Proposed Schedule"}
          </h2>
          <button
            className="proposed-schedule-modal-close"
            onClick={handleClose}
          >
            <BsX />
          </button>
        </div>

        <div className="proposed-schedule-modal-body">
          <div className="proposed-schedule-section">
            <div className="proposed-schedule-roster">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    {callListNames.map((name) => (
                      <th key={name}>{name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scheduleArray.map(({ date, assignments }) => {
                    const momentDate = moment(date);
                    const isWeekendDay = [0, 6].includes(momentDate.day());
                    const isHolidayDay = holidays.some(
                      (h) => moment(h).format("YYYY-MM-DD") === date
                    );
                    const rowClassName = isHolidayDay
                      ? "holiday"
                      : isWeekendDay
                      ? "weekend"
                      : "";

                    return (
                      <tr key={date} className={rowClassName}>
                        <td>{momentDate.format("YYYY-MM-DD")}</td>
                        <td>{momentDate.format("ddd")}</td>
                        {callListNames.map((callListName) => {
                          const assignment = assignments.find(
                            (a) => a.callList.name === callListName
                          );

                          // Empty cell for pre-population mode
                          if (
                            !assignment ||
                            (isPrepopulating && !assignment.doctor)
                          ) {
                            const callList = assignments.find(
                              (a) => a.callList.name === callListName
                            )?.callList;

                            if (!callList) return <td key={callListName}></td>;

                            return (
                              <td key={callListName}>
                                <div
                                  className="empty-cell-placeholder"
                                  onClick={() =>
                                    handleEmptyCellClick(date, callList)
                                  }
                                >
                                  -
                                </div>
                              </td>
                            );
                          }

                          // Regular assignment cell (or pre-populated cell)
                          return (
                            <td key={callListName}>
                              {isPrepopulating ? (
                                <div className="doctor-assignment-container">
                                  <div
                                    onClick={() =>
                                      handleDoctorClick(
                                        date,
                                        assignment.callList,
                                        assignment.doctor
                                      )
                                    }
                                  >
                                    <DoctorChip
                                      doctor={assignment.doctor}
                                      className={getDoctorChipClassName(
                                        date,
                                        assignment
                                      )}
                                      disableTooltip={true}
                                    />
                                  </div>
                                  <button
                                    className="remove-assignment-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveAssignment(
                                        date,
                                        assignment.callList.callListId
                                      );
                                    }}
                                    title="Remove assignment"
                                  >
                                    <BsTrash />
                                  </button>
                                </div>
                              ) : (
                                <div
                                  onClick={() =>
                                    handleDoctorClick(
                                      date,
                                      assignment.callList,
                                      assignment.doctor
                                    )
                                  }
                                >
                                  <DoctorChip
                                    doctor={assignment.doctor}
                                    className={getDoctorChipClassName(
                                      date,
                                      assignment
                                    )}
                                    disableTooltip={true}
                                  />
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="proposed-schedule-doctor-selection">
            <div className="proposed-schedule-selection-panel">
              <div className="proposed-schedule-doctor-selection-title">
                {selectedCell
                  ? "Select Replacement Doctor"
                  : "Click on any doctor to replace"}
              </div>
              {selectedCell && (
                <>
                  <button
                    className="cancel-replacement-button"
                    onClick={() => {
                      setSelectedCell(null);
                      setSelectedReplacement(null);
                      setEligibleDoctors([]);
                    }}
                  >
                    <BsX />
                  </button>
                  <div className="proposed-schedule-doctor-list">
                    {isLoadingDoctors ? (
                      <div className="loading-spinner-container">
                        <div className="doctor-selection-spinner" />
                      </div>
                    ) : (
                      eligibleDoctors.map((doctor) => (
                        <div
                          key={doctor.staffId}
                          onClick={() => {
                            handleDoctorSelect(doctor);
                          }}
                        >
                          <DoctorChip
                            doctor={doctor}
                            className={
                              (selectedCell.currentDoctor &&
                                doctor.staffId ===
                                  selectedCell.currentDoctor.staffId &&
                                !selectedReplacement) ||
                              (selectedReplacement &&
                                doctor.staffId === selectedReplacement.staffId)
                                ? "selected"
                                : ""
                            }
                            disableTooltip={true}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {ruleViolations.length > 0 && (
              <div className="proposed-schedule-violations-panel">
                <div className="proposed-schedule-rule-violations-title">
                  Rule Violations
                </div>
                <div className="proposed-schedule-rule-violations">
                  {ruleViolations.map((violation, index) => (
                    <div
                      key={index}
                      className="proposed-schedule-rule-violation"
                    >
                      {renderViolationMessage(
                        formatViolationMessage(violation)
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="proposed-schedule-modal-actions">
          <button
            className="proposed-schedule-button cancel"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className={`proposed-schedule-button ${
              isPrepopulating ? "continue" : "finalize"
            }`}
            onClick={handleFinalize}
          >
            {isPrepopulating ? "Continue to Generation" : "Finalize Schedule"}
          </button>
        </div>

        {showConfirmation && (
          <div className="proposed-schedule-confirmation-dialog">
            <div className="proposed-schedule-confirmation-message">
              Are you sure you want to finalize this schedule?
            </div>
            <div className="proposed-schedule-confirmation-warning">
              Warning: This schedule contains rule violations.
            </div>
            <div className="proposed-schedule-confirmation-actions">
              <button
                className="proposed-schedule-button cancel"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="proposed-schedule-button finalize"
                onClick={handleConfirmFinalize}
              >
                Confirm Finalize
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposedScheduleModal;
